'use client';

import { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface PrivacyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PrivacyModal({ isOpen, onClose }: PrivacyModalProps) {
  const [privacyContent, setPrivacyContent] = useState<string>('');

  // 서버에서 개인정보처리방침 내용 가져오기
  useEffect(() => {
    async function fetchPrivacyPolicy() {
      try {
        setPrivacyContent('개인정보처리방침을 불러오는 중...');
        
        // 직접 절대경로로 API 호출
        const response = await fetch(`${window.location.origin}/api/privacy`);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`Privacy API error (${response.status}):`, errorText);
          setPrivacyContent(`개인정보처리방침을 불러오는 중 오류가 발생했습니다. (${response.status})`);
          return;
        }
        
        const data = await response.json();
        if (data && data.content) {
          setPrivacyContent(data.content);
        } else {
          console.warn('Privacy API returned empty content:', data);
          setPrivacyContent('개인정보처리방침 내용이 없습니다.');
        }
      } catch (error) {
        console.error('Privacy policy fetch error:', error);
        setPrivacyContent(`개인정보처리방침을 불러오는 중 오류가 발생했습니다: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
      }
    }

    if (isOpen) {
      fetchPrivacyPolicy();
    }
  }, [isOpen]);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={onClose}
                  >
                    <span className="sr-only">닫기</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-xl font-semibold leading-6 text-gray-900 mb-4">
                      개인정보처리방침
                    </Dialog.Title>
                    <div className="mt-2 max-h-[70vh] overflow-y-auto">
                      <div className="prose prose-sm text-gray-500 whitespace-pre-wrap">
                        {privacyContent}
                      </div>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
